import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Shield, Zap, Gem, CloudSun, BadgeIndianRupee } from 'lucide-react';

const params = [
  { icon: Shield, label: 'Mazbooti', color: '#E85A1C', lion: 92, other: 72 },
  { icon: Zap, label: 'Setting Speed', color: '#F5A623', lion: 88, other: 75 },
  { icon: Gem, label: 'Material Purity', color: '#10b981', lion: 95, other: 68 },
  { icon: CloudSun, label: 'Weather Fit', color: '#3b82f6', lion: 90, other: 70 },
  { icon: BadgeIndianRupee, label: 'Paisa Vasool', color: '#a855f7', lion: 95, other: 78 },
];

const StrengthScannerTeaser = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <section ref={ref} className="py-16 md:py-24 bg-void relative overflow-hidden" data-testid="strength-scanner-teaser">
      <div className="absolute inset-0 bg-grid opacity-15" />
      <motion.div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(232,90,28,0.08) 0%, transparent 60%)' }} />

      <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : {}}>
            <span className="text-coral font-mono text-sm mb-4 block">STRENGTH SCANNER</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Why Lion Cement is better than others
            </h2>
            <p className="text-gray-400 text-base mb-8 leading-relaxed">
              5 key parameters mein real performance data dekhiye. Lion Cement ko market alternatives se compare karein — turant, visually, aur bina kisi bias ke.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/strength-scanner" data-testid="teaser-cta-scanner"
                className="group inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-6 py-3 rounded-full transition-all">
                Strength Scanner Try Karein <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/tools" className="group inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-white font-display font-medium px-6 py-3 rounded-full transition-all">
                Saare Tools Dekhein
              </Link>
            </div>
          </motion.div>

          {/* Right: Mini comparison bars */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.2 }}
            className="bg-surface border border-white/5 rounded-2xl p-6 space-y-4">
            {params.map((p, i) => (
              <motion.div key={p.label} initial={{ opacity: 0, y: 10 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3 + i * 0.1 }}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <p.icon className="w-4 h-4" style={{ color: p.color }} />
                    <span className="text-gray-300 text-sm font-display">{p.label}</span>
                  </div>
                  <span className="text-xs text-gray-500 font-mono">Lion vs Market</span>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 h-3 bg-void rounded-full overflow-hidden">
                    <motion.div className="h-full rounded-full" style={{ backgroundColor: '#E85A1C' }}
                      initial={{ width: 0 }} animate={inView ? { width: `${p.lion}%` } : {}} transition={{ duration: 1, delay: 0.5 + i * 0.1 }} />
                  </div>
                  <div className="flex-1 h-3 bg-void rounded-full overflow-hidden">
                    <motion.div className="h-full rounded-full bg-gray-600"
                      initial={{ width: 0 }} animate={inView ? { width: `${p.other}%` } : {}} transition={{ duration: 1, delay: 0.5 + i * 0.1 }} />
                  </div>
                </div>
              </motion.div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-white/5">
              <div className="flex items-center gap-2"><div className="w-3 h-1.5 rounded bg-coral" /><span className="text-xs text-gray-400">Lion Cement</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-1.5 rounded bg-gray-600" /><span className="text-xs text-gray-400">Market Average</span></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StrengthScannerTeaser;
